import React from "react";
import { Link } from "react-router-dom";

import ChillerVanImg from "../../assets/images/ChillerVan/chiller-van-service.jpeg";
import van1Img from "../../assets/images/Home/chiller-van-for-refrigerated-transport.webp";
import sharjahVanImg from "../../assets/images/Home/high-roof-van-service-chiller-dubai-sharjah-rent-freeze.webp";

export const chillerVanSections = [
  {
    type: "cv-hero",
    title: "Chiller Van for Rent in Dubai — Temperature Controlled Vans for Daily Delivery & Distribution Across the City | Hire Today",
    paragraphs: [
      "Chiller Fleet Transport provides chiller vans for rent across Dubai, operating up to 78 vans which can maintain internal cargo temperatures between 0°C and 8°C throughout every delivery. Businesses moving food, dairy products, fresh produce, flowers, and medical supplies across Dubai urban routes rely on our chiller vans for consistent temperature control during short to medium haul distribution cycles.",
      "Both scheduled and same-day rental is available across Dubai, with vehicles dispatch-confirmed and pre-cooled before departure from our Ras Al Khor base. Whether your operation runs fixed daily routes or requires flexible on-demand availability, our fridge van fleet is structured around actual delivery requirements rather than standard hire arrangements.",
      "Contact our team to arrange chiller van rental in Dubai based on your cargo type and delivery schedule.",
    ],
  },

  {
    type: "cv-fleet-table",
    title: "Our Chiller/Fridge Van Specifications & Temperature Range",
    description: "Our chiller vans maintain a consistent internal temperature between 0°C and 8°C throughout transit, suitable for food, dairy, fresh produce, flowers, and pharmaceutical cargo across Dubai routes. The fleet covers payload capacities from 1 ton to 1.5 ton across three vehicle models, each selected for UAE urban delivery conditions and available in both standard roof and high roof configurations.",
    image: ChillerVanImg,
    imageAlt: "Fast chiller van parked indoors for temperature-sensitive deliveries across UAE",
    columns: ["Model", "Payload Capacity", "Roof Type", "Operational Use", "Temp Range", "Rental Duration"],
    rows: [
      {
        model: "Toyota Hiace",
        payload: "1 — 1.5 Ton",
        roof: "Standard & High Roof",
        use: "Daily multi-stop food, dairy & perishable distribution across Dubai",
        temp: "0°C to 8°C",
        duration: "Daily / Weekly / Monthly",
      },
      {
        model: "Nissan NV350 Urvan",
        payload: "Up to 1.5 Ton",
        roof: "Standard & High Roof",
        use: "Small to medium urban delivery operations requiring city maneuverability",
        temp: "0°C to 8°C",
        duration: "Weekly / Monthly",
      },
      {
        model: "Mitsubishi L300",
        payload: "1 Ton",
        roof: "Standard Roof",
        use: "Inner city multi-stop routes requiring fast cooling recovery between stops",
        temp: "0°C to 5°C",
        duration: "Daily / Long Term",
      },
    ],
    note: <>All three models are RTA-compliant, diesel-powered, and fitted with GPS tracking and onboard temperature logging as standard across our Dubai fleet operations. Both rear door and side door access configurations are available depending on loading requirement. For loads exceeding 1.5 tons, our <Link to="/chiller-truck-for-rent" className="internal-link">chiller truck for rent in Dubai</Link> is the correct vehicle category.</>,
  },

  {
    type: "cv-industries",
    title: "When To Use a Chiller Van — Cargo Types & Delivery Situations",
    description: "Chiller vans operate within a 0°C to 8°C range, making them the right vehicle for cargo that requires cooling without freezing. Below are the primary cargo types and delivery situations our fridge van fleet handles across Dubai:",
    note: "",
    industries: [
      {
        icon: "bi-droplet-half",
        title: "Dairy & Fresh Milk Delivery",
        description: "We move dairy products and fresh milk across daily retail supply cycles where temperature consistency across multiple urban stops determines product shelf life. Supermarkets, cafes, and hotel kitchens across Dubai depend on this movement running on schedule every day.",
      },
      {
        icon: "bi-shop",
        title: "Bakery & Confectionery",
        description: "Short shelf life baked goods and confectionery products require a stable cool environment between production facilities and retail points. We handle this movement before heat exposure affects product texture, freshness, or presentation quality.",
      },
      {
        icon: "bi-tree",
        title: "Fresh Produce & Vegetables",
        description: "Restaurant restocking, supermarket replenishment, and market supply runs across Dubai are where our chiller van rental services handle fresh produce movement. Producing freshness at the point of delivery depends entirely on what happens during transit.",
      },
      {
        icon: "bi-flower1",
        title: "Flower & Perishable Delivery",
        description: "UAE summer temperatures degrade flowers within hours without controlled transit conditions. We handle event distribution, wedding logistics, and retail flower supply across Dubai where delivery timing and pre-cooled vehicle preparation determine whether cargo arrives in usable condition.",
      },
      {
        icon: "bi-cup-straw",
        title: "Beverage & Drink Distribution",
        description: "Pre-chilled beverages moving to retail outlets, hotels, and event venues across Dubai require consistent cool temperature throughout transit. We manage this distribution through our chiller van rental in Dubai for operations where warm product on arrival is not an option.",
      },
      {
        icon: "bi-hospital",
        title: "Pharmaceutical & Medical Supply",
        description: <>Temperature-sensitive medications and medical supplies require documented chilled transport conditions during every journey. We operate our fridge van rentals to handle short-haul pharmaceutical movement across UAE routes where cargo integrity and temperature records are both part of the delivery requirement. For bulk pharmaceutical shipments exceeding van capacity, our <Link to="/refrigerated-trucks-for-rent" className="internal-link">refrigerated trucks for rent</Link> provide the necessary scale.</>,
      },
    ],
  },

  {
    type: "cv-pricing",
    title: "Our Chiller Van Rental Fare in Dubai",
    description: "Chiller van rental pricing in Dubai is determined by three factors — rental duration, route distance, and whether a driver is required. All rates include GPS tracking and onboard temperature monitoring as standard. The prices listed below are starting rates — final confirmation is provided based on your specific cargo type, delivery route, and operational requirements. UAE VAT applies where applicable.",
    tiersNote: "All rates are starting prices. Final pricing is confirmed based on cargo type, route distance, and operational requirements. UAE VAT applies where applicable.",
    tiers: [
      {
        label: "Daily Rental",
        note: "Suited for one-off deliveries or businesses testing the service before committing to a longer arrangement. Vehicles are dispatched pre-cooled and route-confirmed for single-day operations.",
        withoutDriver: "AED 250 — AED 350",
        withDriver: "AED 350 — AED 500",
      },
      {
        label: "Weekly Rental",
        note: "Used by businesses running short-term distribution campaigns, seasonal operations, or temporary supply requirements across Dubai.",
        withoutDriver: "AED 1,500 — AED 2,200",
        withDriver: "AED 2,000 — AED 2,800",
      },
      {
        label: "Monthly Rental",
        note: "Monthly arrangements guarantee vehicle availability and provide the lowest per-day rate across all rental tiers — best suited for businesses running fixed daily distribution cycles across Dubai.",
        withoutDriver: "AED 4,500 — AED 6,000",
        withDriver: "AED 6,000 — AED 7,500",
      },
    ],
    routeTables: [
      {
        title: "Chiller Van Route Pricing — From Dubai",
        rows: [
          { from: "Dubai", to: "Sharjah",       withoutDriver: "AED 400", withDriver: "AED 450" },
          { from: "Dubai", to: "Abu Dhabi",      withoutDriver: "AED 400", withDriver: "AED 450" },
          { from: "Dubai", to: "Ajman",          withoutDriver: "AED 420", withDriver: "AED 470" },
          { from: "Dubai", to: "Umm Al Quwain",  withoutDriver: "AED 450", withDriver: "AED 500" },
          { from: "Dubai", to: "Ras Al Khaimah", withoutDriver: "AED 500", withDriver: "AED 550" },
          { from: "Dubai", to: "Fujairah",       withoutDriver: "AED 550", withDriver: "AED 600" },
        ],
      },
    ],
    zoneTables: [
      {
        title: "Dubai Internal Zone Pricing",
        rows: [
          { from: "Ras Al Khor Industrial Area", to: "Business Bay",          withoutDriver: "AED 350", withDriver: "AED 400" },
          { from: "Al Quoz Industrial Area",     to: "Dubai Investment Park", withoutDriver: "AED 350", withDriver: "AED 400" },
          { from: "Deira",                       to: "Bur Dubai",             withoutDriver: "AED 300", withDriver: "AED 350" },
          { from: "Business Bay",                to: "Dubai Marina",          withoutDriver: "AED 300", withDriver: "AED 350" },
          { from: "Jebel Ali Industrial Area",   to: "Dubai South",           withoutDriver: "AED 400", withDriver: "AED 450" },
          { from: "Al Barsha",                   to: "Dubai Marina",          withoutDriver: "AED 320", withDriver: "AED 370" },
        ],
      },
    ],
    note: "All prices are starting rates. Final pricing is confirmed based on cargo type, route, and rental duration. Contact our team for an exact quote.",
  },

  {
    type: "about",
    title: "Chiller Van for Rent in Dubai — Coverage & Delivery Zones",
    paragraphs: [
      "Our Dubai chiller van rental operations are dispatched from Ras Al Khor Industrial Area 1, covering commercial delivery zones including Al Quoz, Deira, Bur Dubai, Jumeirah, Business Bay, and Dubai Industrial Park. Restaurants, cafes, flower shops, pharmacies, and dairy distributors across these zones rely on our fleet for urban multi-stop routing where vehicle availability and departure timing directly affect daily operations.",
      "Same-day chiller van deployment is available from our main branch for Dubai routes. During Ramadan and peak summer months, most Dubai operations are shifted to night and early morning dispatch cycles to manage both traffic congestion and ambient heat exposure across busy commercial corridors.",
    ],
    image: van1Img,
    imageAlt: "White chiller van for refrigerated transport parked outdoors in Dubai",
    reverse: false,
    background: "#f8f9fa",
    actions: [
      { href: "tel:+971544792905",         label: "Call Now",      className: "mini-btn primary" },
      { href: "https://wa.me/971544792905", label: "WhatsApp Now", className: "mini-btn secondary-dark" },
    ],
  },

  {
    type: "about",
    title: "Cross-Emirate Chiller Van Movement From Dubai",
    paragraphs: [
      "Businesses running delivery routes that cross between Dubai and Sharjah book under a single arrangement, with departure coordinated from whichever branch sits closer to the loading point. Route rates from Dubai to Sharjah and the northern emirates are listed in the pricing section above.",
      <>For operations based in Sharjah rather than delivering into it, our <Link to="/chiller-van-for-rent-in-sharjah" className="internal-link">chiller van rental in Sharjah</Link> covers industrial zone and local distribution requirements from our Sharjah branch.</>,
    ],
    image: sharjahVanImg,
    imageAlt: "High roof chiller van for temperature-controlled deliveries across Sharjah and UAE emirates",
    imageMaxHeight: "420px",
    reverse: true,
    background: "#fff",
    actions: [
      { href: "tel:+971544792905",         label: "Call Now",      className: "mini-btn primary" },
      { href: "https://wa.me/971544792905", label: "WhatsApp Now", className: "mini-btn secondary-dark" },
    ],
  },

  {
    type: "cv-why-choose",
    title: "Why Businesses in Dubai Rent Chiller Vans From Chiller Fleet Transport",
    description: "When a chiller van rental company has operated for over 13 years, the operational standards become visible in how bookings are handled. Businesses across Dubai that have worked with multiple refrigerated transport providers consistently return to operators whose fleet is maintained, whose drivers know the routes, and whose dispatch process runs without last-minute surprises. That is what a decade of cold chain operation across the UAE actually produces.",
    reasons: [
      {
        icon: "bi-patch-check",
        title: "RTA-Verified Fleet",
        description: "Every chiller van in our Dubai fleet is RTA-compliant, meeting UAE road and refrigeration transport regulations on every active route.",
      },
      {
        icon: "bi-person-badge",
        title: "Experienced Drivers With Route Familiarity",
        description: "Our drivers have an average of five or more years handling temperature-sensitive cargo across Dubai commercial zones, managing loading coordination and multi-stop sequencing without client supervision.",
      },
      {
        icon: "bi-arrow-repeat",
        title: "Single Booking Across Both Emirates",
        description: "Businesses operating supply routes across Dubai and Sharjah simultaneously are handled under one booking arrangement, with fleet coordinated from both branches without separate contracts or split scheduling.",
      },
      {
        icon: "bi-tools",
        title: "In-House Fleet Maintenance",
        description: "Each branch maintains its own mechanics for regular servicing and refrigeration system checks, keeping vehicles operationally ready rather than dependent on third-party repair schedules.",
      },
    ],
  },

  {
    type: "cv-faq",
    title: "Frequently Asked Questions",
    faqs: [
      {
        q: "What temperature does a chiller van maintain?",
        a: "Our chiller vans maintain a consistent internal temperature between 0°C and 8°C throughout transit. This range covers dairy products, fresh produce, beverages, flowers, and pharmaceutical cargo across all Dubai routes.",
      },
      {
        q: "What is the difference between a chiller van and a freezer van?",
        a: <>Temperature range. Chiller vans maintain 0°C to 8°C for cargo that requires cooling without freezing — dairy, fresh produce, flowers, and pharmaceuticals. <Link to="/freezer-van-for-rent" className="internal-link">Freezer van for rent in Dubai</Link> options maintain 0°C to -18°C for cargo that must remain frozen throughout transit — meat, seafood, ice cream, and packaged frozen goods.</>,
      },
      {
        q: "What is the minimum rental period for a chiller van?",
        a: "Daily rental is the minimum period available. Beyond that, weekly and monthly arrangements are offered at progressively lower per-day rates depending on operational duration and route requirements.",
      },
      {
        q: "Can I rent a chiller van for a single delivery in Dubai?",
        a: "Yes. Same-day single-trip rental is available from our Ras Al Khor dispatch base for Dubai routes. Contact our team with your pickup location and delivery destination to confirm availability.",
      },
      {
        q: "Do your chiller vans come with a driver?",
        a: "Both driver-included and self-drive options are available. Self-drive requires a valid UAE driving license. Driver-included arrangements cover route coordination and loading support throughout the delivery.",
      },
      {
        q: "Is the chiller van available for cross-emirate delivery between Dubai and Sharjah?",
        a: "Yes. Cross-emirate chiller van rental between Dubai and Sharjah is handled through coordinated dispatch from both our Dubai and Sharjah branches under a single booking arrangement.",
      },
      {
        q: "What happens if the refrigeration unit fails during transit?",
        a: "A standby chiller van is deployed from the nearest branch — either Ras Al Khor or our Sharjah branch — to replace the affected vehicle without waiting for it to return to base. Cargo transfer and temperature continuity are managed as part of the replacement process.",
      },
    ],
  },

  {
    type: "cta",
    title: "Book a Chiller Van for Rent in Dubai Today",
    paragraphs: [
      <>Our Dubai fleet is ready for your next chiller van booking. Call or WhatsApp <Link to="/contact-us" className="internal-link">our team</Link> directly — we confirm vehicle availability and collection timing based on your delivery requirement.</>,
    ],
  },
];
